/**
 * Tee-time row matching [Figma Make — New Reservation Card](https://www.figma.com/make/DaVGnkUWa0xomAMEQQ3TWY/New-Reservation-Card)
 * table-style layout (time | course | price | spots | demand).
 */
import React, { useState } from 'react';
import {
  bookingBrandFontFamilyVar,
  teboxDesignSystem,
  useBookingTebox,
} from '../../styles/tebox-booking';
import { formatMoney } from '../../utils/format-money';
import {
  DemandTrendArrowIcon,
  PromoPricingGlyphIcon,
} from './TeeTimePricingMeterGlyphs';
import {
  formatStaffPromoLine,
  formatStaffPromoMeterHeadline,
  formatTime12hSingleLine,
  splitTime12h,
} from './tee-time-ui-copy';
import { useIsMobile } from '../ui';
import { headingTextStyle } from '../../utils/booking-brand-colors';

function formatListRowDateLabel(dateTime: Date): string {
  return dateTime.toLocaleDateString('en-US', {
    month: 'short',
    day: 'numeric',
    year: 'numeric',
  });
}

interface TeeTime {
  id: string;
  courseName: string;
  courseId: string;
  dateTime: Date;
  originalDate?: string;
  price: number;
  capacity: number;
  bookedCount: number;
  availableSpots: number;
  customerTypeId?: string;
  pricingOptions?: Array<{
    customerTypeId: string;
    customerTypeName: string;
    price: number;
    color: string;
  }>;
  memberPlayers?: Array<{
    spotNumber: number;
    firstName: string;
    lastName: string;
    customerType: string;
  }>;
  dynamicPricing?: {
    direction: 'lower' | 'higher';
    percentRounded: number;
    underOnePercent: boolean;
  };
  promotion?: {
    type: 'percentage' | 'fixed';
    value: number;
    discountGreenFee?: boolean;
    discountCart?: boolean;
  };
  priceIncludesHalfCartAddon?: boolean;
}

interface BrandColors {
  primaryColor: string;
  secondaryColor: string;
  textColor: string;
  fontFamily: string;
  headingTextBold?: boolean;
  headingTextItalic?: boolean;
}

export interface TeeTimeListRowProps {
  teeTime: TeeTime;
  onBook: (teeTime: TeeTime) => void;
  isBooking?: boolean;
  /** Logged-in member — omit Demand column and staff promo ribbon. */
  isMember?: boolean;
  brandColors?: BrandColors;
  preloadHalfCartWithGreenFee?: boolean;
  isLast?: boolean;
}

export const TeeTimeListRow: React.FC<TeeTimeListRowProps> = ({
  teeTime,
  onBook,
  isBooking = false,
  isMember = false,
  brandColors: brandColorsProp,
  preloadHalfCartWithGreenFee = false,
  isLast = false,
}) => {
  const includesCartLabel =
    teeTime.priceIncludesHalfCartAddon !== undefined
      ? !!teeTime.priceIncludesHalfCartAddon
      : preloadHalfCartWithGreenFee;

  const tebox = useBookingTebox();
  const ds = teboxDesignSystem;
  const isMobile = useIsMobile();
  const brandColors = brandColorsProp ?? {
    primaryColor: '#000000',
    secondaryColor: tebox.colors.textSecondary,
    textColor: tebox.colors.textPrimary,
    fontFamily: 'Inter',
  };
  const headingTypography = headingTextStyle({
    headingTextBold: brandColors.headingTextBold ?? false,
    headingTextItalic: brandColors.headingTextItalic ?? false,
  });

  const [isHovered, setIsHovered] = useState(false);

  const canBook = teeTime.availableSpots > 0 && !isBooking;

  const priceFootnoteDisplay = includesCartLabel ? 'incl. cart' : 'Per player';
  const timeLabel = formatTime12hSingleLine(teeTime.dateTime);
  const timeDisplayParts = splitTime12h(teeTime.dateTime);

  const spotsLine =
    teeTime.availableSpots === 0
      ? 'Full'
      : teeTime.availableSpots === 1
        ? '1 spot'
        : `${teeTime.availableSpots} spots`;
  const spotsAvailablePhrase =
    teeTime.availableSpots === 0
      ? 'no spots available'
      : teeTime.availableSpots === 1
        ? '1 spot available'
        : `${teeTime.availableSpots} spots available`;

  const staffPromo = teeTime.promotion;
  const dp = staffPromo ? undefined : teeTime.dynamicPricing;
  const percentPhrase = dp
    ? dp.underOnePercent
      ? 'Under 1%'
      : `${dp.percentRounded}%`
    : '';

  const demandHasTrend = staffPromo == null && dp != null && percentPhrase !== '';

  const handleActivate = () => {
    if (canBook) onBook(teeTime);
  };

  const handleKeyDown = (e: React.KeyboardEvent) => {
    if (!canBook) return;
    if (e.key === 'Enter' || e.key === ' ') {
      e.preventDefault();
      handleActivate();
    }
  };

  const showGuestDemandPromoUi = !isMember;
  const promoMeterActive = staffPromo != null;
  const showGuestPricingMeterRow =
    showGuestDemandPromoUi && (promoMeterActive || demandHasTrend);

  const ariaDynamic =
    isMember ? '' : staffPromo != null
      ? `, ${formatStaffPromoLine(staffPromo)}`
      : dp && percentPhrase
        ? `, ${percentPhrase} ${dp.direction === 'lower' ? 'below' : 'above'} standard ${includesCartLabel ? 'green fee ' : ''}rate`
        : '';

  const ariaPerPlayer = includesCartLabel
    ? 'per player, cart included with green fee'
    : 'per player';

  const ink = ds.colors.text.primary;
  const muted = tebox.colors.textSecondary;

  const gridColsGuest =
    'minmax(5.25rem,1fr) minmax(6.75rem,1.2fr) minmax(5.75rem,0.95fr) minmax(4.1rem,0.8fr) minmax(4.25rem,0.82fr)';
  const gridColsMember =
    'minmax(5.25rem,1fr) minmax(6.75rem,1.3fr) minmax(6.25rem,1fr) minmax(4.5rem,0.9fr)';
  const gridCols = showGuestPricingMeterRow ? gridColsGuest : gridColsMember;

  const dateLabel = formatListRowDateLabel(teeTime.dateTime);

  const rowSurface =
    canBook && isHovered ? tebox.colors.bgHover : tebox.colors.bgPrimary;

  const secondaryCaptionStyle: React.CSSProperties = {
    fontFamily: bookingBrandFontFamilyVar,
    fontWeight: 400,
    fontSize: ds.typography.caption.fontSize,
    lineHeight: 1.25,
    color: muted,
    margin: 0,
    padding: 0,
  };

  /** Grid row 1 — primary band: time numerals, price, spots, demand (`body` + semibold + tabular nums where relevant). */
  const topRowPrimaryBand: React.CSSProperties = {
    fontFamily: bookingBrandFontFamilyVar,
    fontSize: ds.typography.body.fontSize,
    lineHeight: ds.typography.body.lineHeight,
    letterSpacing: ds.typography.body.letterSpacing,
    fontVariantNumeric: 'tabular-nums',
    color: ink,
    ...headingTypography,
  };

  const topRowTextBand: React.CSSProperties = {
    fontFamily: bookingBrandFontFamilyVar,
    fontWeight: 500,
    fontSize: ds.typography.body.fontSize,
    lineHeight: ds.typography.body.lineHeight,
    letterSpacing: ds.typography.body.letterSpacing,
    color: ink,
  };

  const listRowTimeStyle: React.CSSProperties = {
    fontFamily: bookingBrandFontFamilyVar,
    fontSize: tebox.teeTimeListRow.timeFontSize,
    lineHeight: ds.typography.body.lineHeight,
    letterSpacing: ds.typography.body.letterSpacing,
    fontVariantNumeric: 'tabular-nums',
    color: ink,
    ...headingTypography,
  };

  const meterChipPx = tebox.teeTimeCard.demandTrendCircleDiameterPx;
  const meterGlyphPx = Math.round(meterChipPx * 0.5);
  const meterChipBg = ds.colors.gray[100];

  return (
    <div
      role="button"
      tabIndex={canBook ? 0 : -1}
      aria-disabled={!canBook}
      aria-label={
        canBook
          ? `Book ${teeTime.courseName} on ${dateLabel} at ${timeLabel}, ${spotsAvailablePhrase}, $${formatMoney(
              teeTime.price,
            )} ${ariaPerPlayer}${ariaDynamic}`
          : `Tee time unavailable, ${teeTime.courseName} on ${dateLabel} at ${timeLabel}`
      }
      className="tee-time-card-shadow focus-visible:outline focus-visible:outline-1 focus-visible:outline-offset-1 focus-visible:outline-[var(--tebox-focus-ring)]"
      style={{
        width: '100%',
        boxSizing: 'border-box',
        borderBottom: isLast ? undefined : `1px solid ${tebox.colors.border}`,
        backgroundColor: rowSurface,
        cursor: canBook ? 'pointer' : 'not-allowed',
        opacity: canBook ? 1 : 0.78,
        transition: 'background-color 0.15s ease',
      }}
      onMouseEnter={() => setIsHovered(true)}
      onMouseLeave={() => setIsHovered(false)}
      onClick={handleActivate}
      onKeyDown={handleKeyDown}
    >
      <div
        style={{
          padding: isMobile ? `${ds.spacing.sm} ${ds.spacing.sm}` : `14px ${ds.spacing.md}`,
          boxSizing: 'border-box',
        }}
      >
        {isMobile ? (
          <div
            style={{
              display: 'flex',
              flexDirection: 'column',
              gap: ds.spacing.sm,
              width: '100%',
              boxSizing: 'border-box',
            }}
          >
            <div
              style={{
                display: 'flex',
                flexDirection: 'row',
                justifyContent: 'space-between',
                alignItems: 'flex-start',
                gap: ds.spacing.md,
                width: '100%',
                minWidth: 0,
              }}
            >
              <div
                style={{
                  display: 'flex',
                  flexDirection: 'row',
                  alignItems: 'center',
                  gap: ds.spacing.sm,
                  flexShrink: 0,
                }}
              >
                <span
                  style={{
                    ...listRowTimeStyle,
                    fontSize: tebox.teeTimeCard.clockFontSize,
                  }}
                >
                  {timeDisplayParts.clock}
                </span>
                <span
                  style={{
                    ...listRowTimeStyle,
                    fontSize: ds.typography.body.fontSize,
                  }}
                >
                  {timeDisplayParts.meridiem}
                </span>
              </div>
              <div
                style={{
                  display: 'flex',
                  flexDirection: 'column',
                  alignItems: 'flex-end',
                  textAlign: 'right',
                  minWidth: 0,
                }}
              >
                <div style={topRowPrimaryBand}>${formatMoney(teeTime.price)}</div>
                <p style={{ ...secondaryCaptionStyle }}>{priceFootnoteDisplay}</p>
                <p style={{ ...secondaryCaptionStyle, fontWeight: 500 }} aria-label={`Date ${dateLabel}`}>
                  {dateLabel}
                </p>
              </div>
            </div>

            <div
              style={{
                ...topRowTextBand,
                minWidth: 0,
                overflow: 'hidden',
                display: '-webkit-box',
                WebkitLineClamp: 2,
                WebkitBoxOrient: 'vertical',
                wordBreak: 'break-word',
              }}
              title={teeTime.courseName}
            >
              {teeTime.courseName}
            </div>

            <div
              style={{
                display: 'flex',
                flexDirection: 'row',
                flexWrap: 'wrap',
                alignItems: 'center',
                gap: ds.spacing.sm,
                minWidth: 0,
              }}
            >
              <span style={topRowPrimaryBand} aria-label={spotsAvailablePhrase}>
                {spotsLine}
              </span>
              {showGuestPricingMeterRow ? (
                <span
                  style={{
                    ...topRowPrimaryBand,
                    display: 'inline-flex',
                    flexDirection: 'row',
                    alignItems: 'center',
                    gap: ds.spacing.xs,
                  }}
                  title={
                    promoMeterActive ? formatStaffPromoLine(staffPromo!) : undefined
                  }
                >
                  <span
                    style={{
                      display: 'inline-flex',
                      alignItems: 'center',
                      justifyContent: 'center',
                      width: meterChipPx,
                      height: meterChipPx,
                      borderRadius: ds.borderRadius.full,
                      backgroundColor: meterChipBg,
                      flexShrink: 0,
                    }}
                  >
                    {promoMeterActive ? (
                      <PromoPricingGlyphIcon
                        sizePx={meterGlyphPx}
                        stroke={ink}
                        strokeWidth={2.25}
                      />
                    ) : (
                      <DemandTrendArrowIcon
                        direction={dp!.direction}
                        sizePx={meterGlyphPx}
                        stroke={ink}
                        strokeWidth={2.25}
                      />
                    )}
                  </span>
                  <span>
                    {promoMeterActive
                      ? formatStaffPromoMeterHeadline(staffPromo!)
                      : percentPhrase}
                  </span>
                </span>
              ) : null}
            </div>
          </div>
        ) : (
          <div
            style={{
              display: 'grid',
              gridTemplateColumns: gridCols,
              gridTemplateRows: 'auto auto',
              columnGap: ds.spacing.sm,
              rowGap: 0,
              alignItems: 'center',
              boxSizing: 'border-box',
            }}
          >
          {/* Row 1–2 — time spans both lines so it centers vs primary + caption band */}
          <div
            style={{
              gridColumn: 1,
              gridRow: '1 / 3',
              display: 'flex',
              flexDirection: 'row',
              alignItems: 'center',
              justifyContent: 'flex-start',
              gap: ds.spacing.sm,
              minWidth: 0,
            }}
          >
            <span
              style={{
                ...listRowTimeStyle,
              }}
            >
              {timeDisplayParts.clock}
            </span>
            <span
              style={{
                ...listRowTimeStyle,
                fontSize: ds.typography.body.fontSize,
              }}
            >
              {timeDisplayParts.meridiem}
            </span>
          </div>

          <div
            style={{
              gridColumn: 2,
              gridRow: 1,
              minWidth: 0,
              textAlign: 'right',
              ...topRowTextBand,
              overflow: 'hidden',
              textOverflow: 'ellipsis',
              whiteSpace: 'nowrap',
            }}
            title={teeTime.courseName}
          >
            {teeTime.courseName}
          </div>

          <div
            style={{
              gridColumn: 3,
              gridRow: 1,
              textAlign: 'right',
              minWidth: 0,
              ...topRowPrimaryBand,
            }}
          >
            ${formatMoney(teeTime.price)}
          </div>

          <div
            style={{
              gridColumn: 4,
              gridRow: 1,
              textAlign: 'right',
              minWidth: 0,
              ...topRowPrimaryBand,
            }}
            aria-label={spotsAvailablePhrase}
          >
            {spotsLine}
          </div>

          {showGuestPricingMeterRow ? (
            <>
              <div
                style={{
                  gridColumn: 5,
                  gridRow: 1,
                  textAlign: 'right',
                  minWidth: 0,
                  ...topRowPrimaryBand,
                  display: 'flex',
                  flexDirection: 'row',
                  alignItems: 'center',
                  justifyContent: 'flex-end',
                  gap: ds.spacing.xs,
                }}
                title={
                  promoMeterActive ? formatStaffPromoLine(staffPromo!) : undefined
                }
              >
                <span
                  style={{
                    display: 'inline-flex',
                    alignItems: 'center',
                    justifyContent: 'center',
                    width: meterChipPx,
                    height: meterChipPx,
                    borderRadius: ds.borderRadius.full,
                    backgroundColor: meterChipBg,
                    flexShrink: 0,
                  }}
                >
                  {promoMeterActive ? (
                    <PromoPricingGlyphIcon
                      sizePx={meterGlyphPx}
                      stroke={ink}
                      strokeWidth={2.25}
                    />
                  ) : (
                    <DemandTrendArrowIcon
                      direction={dp!.direction}
                      sizePx={meterGlyphPx}
                      stroke={ink}
                      strokeWidth={2.25}
                    />
                  )}
                </span>
                <span>
                  {promoMeterActive
                    ? formatStaffPromoMeterHeadline(staffPromo!)
                    : percentPhrase}
                </span>
              </div>

              <p style={{ ...secondaryCaptionStyle, gridColumn: 5, gridRow: 2, textAlign: 'right' }}>
                {promoMeterActive ? 'Promo' : 'Demand'}
              </p>
            </>
          ) : null}

          {/* Row 2 — captions: date | cart | Avail */}
          <p
            style={{
              ...secondaryCaptionStyle,
              gridColumn: 2,
              gridRow: 2,
              textAlign: 'right',
              fontWeight: 500,
            }}
            aria-label={`Date ${dateLabel}`}
          >
            {dateLabel}
          </p>

          <p style={{ ...secondaryCaptionStyle, gridColumn: 3, gridRow: 2, textAlign: 'right' }}>
            {priceFootnoteDisplay}
          </p>

          <p style={{ ...secondaryCaptionStyle, gridColumn: 4, gridRow: 2, textAlign: 'right' }}>
            Avail
          </p>
        </div>
        )}
      </div>

    </div>
  );
};
